/* eslint-disable react-hooks/rules-of-hooks, react-hooks/exhaustive-deps */
import { useState, useEffect, useMemo, useRef } from "react";
import { SHIFTIQ_API, getAuthHeaders } from "../../api/client";
import { usePgStores } from "../../stores/StoresProvider";
import { Icon, LoadingPulse } from "../../ui";
import { toLocalISO, parseISODateLocal } from "../../lib/dates";
import { defaultBuilderStore, userStorePinLabel } from "../../theme";
import { isEodStockPhysicalStore, formatEodStockHeaderDate, formatEodStockTime, searchForStorePage, pageFromPathname, replaceBrowserPath } from "../../routing/paths";
import { allowedStoresForUser, isSingleStoreLocked, locationIdForStore } from "../../lib/storeScope";
import { resolveSharedStoreName, rememberStoreByName } from "../../lib/selectedStore";

export function EndOfDayStockPage({ user, lang }) {
  const { storeNames, storeRecords, loaded: storesLoaded } = usePgStores();
  const allowed = allowedStoresForUser(user);
  const hasPhysicalHomeStore = Boolean(
    (Array.isArray(allowed) && allowed.some(isEodStockPhysicalStore)) ||
      (user?.store && isEodStockPhysicalStore(user.store))
  );
  const isStoreLocked = isSingleStoreLocked(user) && hasPhysicalHomeStore;
  const canPickStore =
    user.role === "admin" ||
    user.role === "brand_manager" ||
    (Array.isArray(allowed) && allowed.length > 1) ||
    (!isStoreLocked && !hasPhysicalHomeStore);
  const eodStores = useMemo(() => {
    if (Array.isArray(allowed) && allowed.length) {
      return allowed.filter((n) => isEodStockPhysicalStore(n));
    }
    return (storeNames || []).filter((n) => n && n !== "All Stores");
  }, [allowed, storeNames]);

  const defaultStore = useMemo(() => {
    if (isStoreLocked && allowed?.[0] && isEodStockPhysicalStore(allowed[0])) return allowed[0];
    return defaultBuilderStore(eodStores, storeRecords);
  }, [isStoreLocked, allowed, eodStores, storeRecords]);

  const todayISO = toLocalISO(new Date());
  const [selectedStore, setSelectedStore] = useState(() => {
    if (isStoreLocked && allowed?.[0] && isEodStockPhysicalStore(allowed[0])) return allowed[0];
    return resolveSharedStoreName({
      search: typeof window !== "undefined" ? window.location.search : "",
      storeRecords,
      storeNames: eodStores,
    }) || defaultStore;
  });
  const [selectedDate, setSelectedDate] = useState(todayISO);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [kgByName, setKgByName] = useState({});
  const [addedItems, setAddedItems] = useState([]);
  const [showAddPicker, setShowAddPicker] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [lastSubmittedAt, setLastSubmittedAt] = useState(null);

  const [reloadTick, setReloadTick] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshSpinKey, setRefreshSpinKey] = useState(0);
  const refreshAbortRef = useRef(null);

  const appliedSharedStore = useRef(false);
  useEffect(() => {
    if (!storesLoaded) return;
    if (Array.isArray(allowed) && allowed.length === 1 && isEodStockPhysicalStore(allowed[0])) {
      setSelectedStore(allowed[0]);
      return;
    }
    if (Array.isArray(allowed) && allowed.length > 1) {
      setSelectedStore((prev) => (allowed.includes(prev) ? prev : allowed[0]));
      return;
    }
    if (appliedSharedStore.current || !eodStores.length) return;
    appliedSharedStore.current = true;
    const preferred = resolveSharedStoreName({
      search: typeof window !== "undefined" ? window.location.search : "",
      storeRecords,
      storeNames: eodStores,
    });
    const next = preferred && eodStores.includes(preferred) ? preferred : defaultStore;
    if (next) setSelectedStore(next);
  }, [storesLoaded, storeRecords, isStoreLocked, user?.store, eodStores, defaultStore]);

  useEffect(() => {
    if (!storesLoaded || pageFromPathname(window.location.pathname) !== "eod-stock") return undefined;
    const locationId = locationIdForStore(selectedStore, storeRecords);
    replaceBrowserPath("/eod-stock", searchForStorePage({ locationId }));
    return undefined;
  }, [storesLoaded, selectedStore, storeRecords]);
  const daysBack = useMemo(() => {
    const a = parseISODateLocal(todayISO);
    const b = parseISODateLocal(selectedDate);
    return Math.round((a - b) / (24 * 60 * 60 * 1000));
  }, [todayISO, selectedDate]);

  const canGoBack = daysBack < 7;
  const canGoForward = selectedDate < todayISO;

  useEffect(() => {
    if (!selectedStore) {
      setLoadError(lang === "gr" ? "Δεν επιλέχθηκε κατάστημα" : "No store selected");
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    refreshAbortRef.current?.abort();
    refreshAbortRef.current = controller;
    setLoading(true);
    setLoadError(null);
    setSubmitError("");
    fetch(
      `${SHIFTIQ_API}/api/kitchen/stock-log/items?location=${encodeURIComponent(selectedStore)}&date=${selectedDate}`,
      { headers: getAuthHeaders(), signal: controller.signal }
    )
      .then(async (r) => {
        if (!r.ok) {
          let detail = lang === "gr" ? "Αποτυχία φόρτωσης" : "Failed to load";
          try {
            const body = await r.json();
            if (typeof body?.detail === "string") detail = body.detail;
          } catch { /* ignore */ }
          throw new Error(detail);
        }
        return r.json();
      })
      .then(async (data) => {
        if (controller.signal.aborted) return;
        setPayload(data);
        const nextKg = {};
        (data.items || []).forEach((item) => {
          const name = item.canonical_name;
          if (!name) return;
          if (item.submitted_closing_kg != null && item.submitted_closing_kg !== "") {
            nextKg[name] = String(item.submitted_closing_kg);
          } else {
            nextKg[name] = "";
          }
        });
        setKgByName(nextKg);
        setAddedItems([]);
        setShowAddPicker(false);

        if (data.already_submitted) {
          try {
            const r2 = await fetch(
              `${SHIFTIQ_API}/api/kitchen/stock-log?location=${encodeURIComponent(selectedStore)}&date=${selectedDate}`,
              { headers: getAuthHeaders(), signal: controller.signal }
            );
            if (controller.signal.aborted) return;
            if (r2.ok) {
              const d2 = await r2.json();
              setLastSubmittedAt(d2.submitted_at || null);
            } else {
              setLastSubmittedAt(null);
            }
          } catch (err) {
            if (controller.signal.aborted || err?.name === "AbortError") return;
            setLastSubmittedAt(null);
          }
        } else {
          setLastSubmittedAt(null);
        }
      })
      .catch((err) => {
        if (controller.signal.aborted || err?.name === "AbortError") return;
        setLoadError(err?.message || (lang === "gr" ? "Αποτυχία φόρτωσης" : "Failed to load"));
        setPayload(null);
        setLastSubmittedAt(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [selectedStore, selectedDate, lang, reloadTick]);

  async function refreshCookedItems() {
    if (!selectedStore || refreshing) return;
    refreshAbortRef.current?.abort();
    const controller = new AbortController();
    refreshAbortRef.current = controller;
    setRefreshSpinKey((k) => k + 1);
    setRefreshing(true);
    setSubmitError("");
    try {
      const r = await fetch(
        `${SHIFTIQ_API}/api/kitchen/stock-log/items?location=${encodeURIComponent(selectedStore)}&date=${selectedDate}`,
        { headers: getAuthHeaders(), signal: controller.signal }
      );
      if (!r.ok) {
        let detail = lang === "gr" ? "Αποτυχία ανανέωσης" : "Failed to refresh";
        try {
          const body = await r.json();
          if (typeof body?.detail === "string") detail = body.detail;
        } catch { /* ignore */ }
        throw new Error(detail);
      }
      const data = await r.json();
      const nextItems = data.items || [];
      const apiNames = new Set(nextItems.map((i) => i.canonical_name).filter(Boolean));

      setPayload((prev) => ({
        ...(prev || {}),
        ...data,
        items: nextItems,
      }));

      setKgByName((prev) => {
        const next = { ...prev };
        nextItems.forEach((item) => {
          const name = item.canonical_name;
          if (!name) return;
          if (!(name in next)) {
            next[name] = "";
          }
        });
        return next;
      });

      // Drop manually-added rows that now appear in the API list
      setAddedItems((prev) => prev.filter((i) => !apiNames.has(i.canonical_name)));
    } catch (err) {
      if (controller.signal.aborted || err?.name === "AbortError") return;
      setSubmitError(err?.message || (lang === "gr" ? "Αποτυχία ανανέωσης" : "Failed to refresh"));
    } finally {
      if (!controller.signal.aborted) setRefreshing(false);
    }
  }

  const cookedItems = (payload?.items || []).filter((i) => i.source === "cooked_today");
  const carryItems = (payload?.items || []).filter((i) => i.source === "carry_over");
  const listedNames = useMemo(() => {
    const names = new Set([
      ...(payload?.items || []).map((i) => i.canonical_name),
      ...addedItems.map((i) => i.canonical_name),
    ]);
    return names;
  }, [payload, addedItems]);

  const otherRecipesAvailable = (payload?.other_recipes || []).filter(
    (r) => r.canonical_name && !listedNames.has(r.canonical_name)
  );

  function setKg(name, value) {
    setKgByName((prev) => ({ ...prev, [name]: value }));
  }

  function addOtherRecipe(recipe) {
    if (!recipe?.canonical_name) return;
    setAddedItems((prev) => [
      ...prev,
      {
        canonical_name: recipe.canonical_name,
        recipe_master_id: recipe.recipe_master_id,
        source: "other",
      },
    ]);
    setKgByName((prev) => ({ ...prev, [recipe.canonical_name]: prev[recipe.canonical_name] ?? "0" }));
    setShowAddPicker(false);
  }

  function removeAddedItem(name) {
    setAddedItems((prev) => prev.filter((i) => i.canonical_name !== name));
    setKgByName((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }

  function shiftDate(deltaDays) {
    const d = parseISODateLocal(selectedDate);
    d.setDate(d.getDate() + deltaDays);
    const next = toLocalISO(d);
    const today = toLocalISO(new Date());
    const earliest = parseISODateLocal(today);
    earliest.setDate(earliest.getDate() - 7);
    const earliestISO = toLocalISO(earliest);
    if (next > today || next < earliestISO) return;
    setSelectedDate(next);
  }

  async function handleSubmit() {
    setSubmitting(true);
    setSubmitError("");
    setSubmitSuccess("");
    const allRows = [
      ...(payload?.items || []),
      ...addedItems,
    ];
    const items = allRows.map((row) => {
      const raw = kgByName[row.canonical_name];
      const num = raw === "" || raw == null ? 0 : Number(raw);
      return {
        canonical_name: row.canonical_name,
        recipe_master_id: row.recipe_master_id ?? null,
        closing_stock_kg: Number.isFinite(num) && num >= 0 ? num : 0,
        notes: null,
      };
    });

    try {
      const r = await fetch(`${SHIFTIQ_API}/api/kitchen/stock-log`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          location: selectedStore,
          date: selectedDate,
          submitted_by_user_id: user?.id ?? null,
          items,
        }),
      });
      if (!r.ok) {
        let detail = lang === "gr" ? "Αποτυχία υποβολής" : "Submit failed";
        try {
          const body = await r.json();
          if (typeof body?.detail === "string") detail = body.detail;
        } catch { /* ignore */ }
        throw new Error(detail);
      }
      const data = await r.json();
      const submittedAt =
        data?.items?.map((i) => i.submitted_at).filter(Boolean).sort().slice(-1)[0]
        || new Date().toISOString();
      const timeLabel = formatEodStockTime(submittedAt, lang);
      setSubmitSuccess(
        lang === "gr" ? `Υποβλήθηκε ✓ ${timeLabel}` : `Submitted ✓ ${timeLabel}`
      );
      setLastSubmittedAt(submittedAt);
      setReloadTick((t) => t + 1);
    } catch (err) {
      setSubmitError(err?.message || (lang === "gr" ? "Αποτυχία υποβολής" : "Submit failed"));
    } finally {
      setSubmitting(false);
    }
  }

  function renderStockRow(item, { removable = false, yesterdayKg = null } = {}) {
    const name = item.canonical_name;
    const hasSubmitted = item.submitted_closing_kg != null && item.submitted_closing_kg !== "";
    const rawVal = kgByName[name] ?? "";
    const numVal = rawVal === "" || rawVal == null ? null : Number(rawVal);
    const showHighWarning = Number.isFinite(numVal) && numVal > 50;
    return (
      <div
        key={name}
        className="leave-card"
        style={{ alignItems: "flex-start", gap: 10, padding: "12px 14px" }}
      >
        <div style={{ flex: 1, minWidth: 0, paddingTop: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: "var(--text-primary)" }}>
              {name}
            </span>
            {hasSubmitted && (
              <span
                title={lang === "gr" ? "Έχει υποβληθεί" : "Already submitted"}
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: "var(--green)",
                  flexShrink: 0,
                }}
              />
            )}
          </div>
          {yesterdayKg != null && (
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 3, fontWeight: 600 }}>
              {lang === "gr" ? `Χθες: ${yesterdayKg} kg` : `Yesterday: ${yesterdayKg} kg`}
            </div>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, maxWidth: 160 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="number"
              className="form-input"
              min={0}
              step={0.1}
              value={rawVal}
              onChange={(e) => setKg(name, e.target.value)}
              placeholder="0"
              style={{ width: 88, textAlign: "right", marginBottom: 0, padding: "8px 10px" }}
            />
            <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", width: 22 }}>
              kg
            </span>
            {removable && (
              <button
                type="button"
                className="btn-base btn-danger-soft btn-small"
                style={{ padding: "7px 9px" }}
                onClick={() => removeAddedItem(name)}
                aria-label="Remove"
              >
                <Icon name="close" size={13} />
              </button>
            )}
          </div>
          <div style={{ fontSize: 10, color: "var(--text-muted)", fontWeight: 500, lineHeight: 1.3, textAlign: "right" }}>
            {lang === "gr" ? "Σε kg (π.χ. 2.5 για 2.5 kg)" : "Enter kg (e.g. 2.5 for 2.5 kg)"}
          </div>
          {showHighWarning && (
            <div style={{ fontSize: 11, color: "#d97706", fontWeight: 600, lineHeight: 1.35, textAlign: "right" }}>
              {lang === "gr"
                ? "Υψηλή τιμή — επιβεβαιώστε ότι είναι σε kg, όχι γραμμάρια"
                : "Value seems high — please confirm this is in kg, not grams"}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!isEodStockPhysicalStore(selectedStore) && isStoreLocked) {
    return (
      <div className="anim-fade empty-state">
        <Icon name="clipboard" size={40} />
        <p>{lang === "gr" ? "Μη διαθέσιμο για αυτό το κατάστημα" : "Not available for this location"}</p>
      </div>
    );
  }

  return (
    <div className="anim-fade">
      {canPickStore && (
        <div className="filter-row" style={{ marginBottom: 16 }}>
          {eodStores.map((s) => (
            <button
              key={s}
              type="button"
              className={`filter-chip ${selectedStore === s ? "chip-active" : ""}`}
              onClick={() => {
                setSelectedStore(s);
                rememberStoreByName(s, storeRecords);
              }}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {isStoreLocked && user.store && (
        <div style={{ fontWeight: 800, fontSize: 14, color: "var(--text-primary)", marginBottom: 12 }}>
          📍 {userStorePinLabel(user)}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <button
          type="button"
          className="btn-base btn-small"
          style={{
            padding: "8px 10px",
            background: "var(--surface2)",
            color: "var(--text-primary)",
            opacity: canGoBack ? 1 : 0.4,
          }}
          disabled={!canGoBack}
          onClick={() => shiftDate(-1)}
          aria-label="Previous day"
        >
          <Icon name="back" size={14} />
        </button>
        <div style={{ flex: 1, textAlign: "center" }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: "var(--text-primary)", lineHeight: 1.3 }}>
            {lang === "gr" ? "Απόθεμα ημέρας" : "End of Day Stock"}
            {" — "}
            {formatEodStockHeaderDate(selectedDate, lang)}
          </div>
        </div>
        <button
          type="button"
          className="btn-base btn-small"
          style={{
            padding: "8px 10px",
            background: "var(--surface2)",
            color: "var(--text-primary)",
            opacity: canGoForward ? 1 : 0.4,
            transform: "scaleX(-1)",
          }}
          disabled={!canGoForward}
          onClick={() => shiftDate(1)}
          aria-label="Next day"
        >
          <Icon name="back" size={14} />
        </button>
      </div>

      {lastSubmittedAt && (
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--green)", marginBottom: 14, textAlign: "center" }}>
          {lang === "gr" ? "Τελευταία υποβολή: " : "Last submitted: "}
          {formatEodStockTime(lastSubmittedAt, lang)}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: 48 }}>
          <LoadingPulse size={28} />
          <div style={{ fontSize: 13, color: "var(--text-muted)", fontWeight: 600, marginTop: 12 }}>
            {lang === "gr" ? "Φόρτωση..." : "Loading..."}
          </div>
        </div>
      ) : loadError ? (
        <div style={{ fontSize: 13, color: "var(--danger)", fontWeight: 600, padding: "12px 0" }}>
          {loadError}
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 10 }}>
              <div style={{ fontWeight: 800, fontSize: 13, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 1 }}>
                {lang === "gr" ? "Μαγειρεμένα σήμερα" : "Cooked today"}
              </div>
              <button
                type="button"
                onClick={refreshCookedItems}
                disabled={refreshing}
                aria-label={lang === "gr" ? "Ανανέωση" : "Refresh"}
                title={
                  lang === "gr"
                    ? "Πατήστε για φόρτωση ειδών που μαγειρεύτηκαν αφού ανοίξατε τη σελίδα"
                    : "Tap to load items cooked since this page opened"
                }
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  flexShrink: 0,
                  padding: "4px 10px",
                  margin: 0,
                  border: "1.5px solid var(--green)",
                  borderRadius: 999,
                  background: "var(--surface)",
                  color: "var(--green)",
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: refreshing ? "default" : "pointer",
                  lineHeight: 1,
                  opacity: refreshing ? 0.55 : 1,
                }}
              >
                <svg
                  key={refreshSpinKey}
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  style={{
                    animation: refreshSpinKey > 0 ? "spin 0.5s linear" : "none",
                  }}
                >
                  <polyline points="23 4 23 10 17 10"></polyline>
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                </svg>
                {lang === "gr" ? "Ανανέωση" : "Refresh"}
              </button>
            </div>
            {cookedItems.length === 0 ? (
              <div style={{ fontSize: 13, color: "var(--text-muted)", fontWeight: 600, padding: "8px 0" }}>
                {lang === "gr" ? "Κανένα μαγείρεμα σήμερα" : "No items cooked today"}
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {cookedItems.map((item) => renderStockRow(item))}
              </div>
            )}
          </div>

          {carryItems.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontWeight: 800, fontSize: 13, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>
                {lang === "gr" ? "Μεταφορά από χθες" : "Carry-over items"}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {carryItems.map((item) =>
                  renderStockRow(item, { yesterdayKg: item.yesterday_closing_kg })
                )}
              </div>
            </div>
          )}

          {addedItems.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontWeight: 800, fontSize: 13, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>
                {lang === "gr" ? "Προστέθηκαν" : "Added items"}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {addedItems.map((item) => renderStockRow(item, { removable: true }))}
              </div>
            </div>
          )}

          <div style={{ marginBottom: 20 }}>
            {!showAddPicker ? (
              <button
                type="button"
                className="btn-base btn-small"
                style={{ background: "var(--surface2)", color: "var(--text-primary)" }}
                onClick={() => setShowAddPicker(true)}
                disabled={otherRecipesAvailable.length === 0}
              >
                <Icon name="plus" size={13} />
                {lang === "gr" ? "Προσθήκη είδους" : "Add item"}
              </button>
            ) : (
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">
                  {lang === "gr" ? "Επιλογή είδους" : "Select item"}
                </label>
                <select
                  className="form-select"
                  defaultValue=""
                  onChange={(e) => {
                    const name = e.target.value;
                    if (!name) return;
                    const recipe = otherRecipesAvailable.find((r) => r.canonical_name === name);
                    if (recipe) addOtherRecipe(recipe);
                  }}
                >
                  <option value="">
                    {lang === "gr" ? "— Επιλέξτε —" : "— Choose —"}
                  </option>
                  {otherRecipesAvailable.map((r) => (
                    <option key={r.canonical_name} value={r.canonical_name}>
                      {r.canonical_name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn-base btn-small"
                  style={{ marginTop: 8, background: "var(--surface2)", color: "var(--text-muted)" }}
                  onClick={() => setShowAddPicker(false)}
                >
                  {lang === "gr" ? "Ακύρωση" : "Cancel"}
                </button>
              </div>
            )}
          </div>

          {submitError && (
            <div style={{ fontSize: 13, color: "var(--danger)", fontWeight: 600, marginBottom: 12 }}>
              {submitError}
            </div>
          )}
          {submitSuccess && (
            <div style={{ fontSize: 13, color: "var(--green)", fontWeight: 700, marginBottom: 12 }}>
              {submitSuccess}
            </div>
          )}

          <button
            type="button"
            className="btn-base btn-green"
            style={{ width: "100%", justifyContent: "center" }}
            disabled={submitting || (!cookedItems.length && !carryItems.length && !addedItems.length)}
            onClick={handleSubmit}
          >
            {submitting
              ? (lang === "gr" ? "Υποβολή..." : "Submitting...")
              : payload?.already_submitted
                ? (lang === "gr" ? "Ενημέρωση αποθέματος" : "Update closing stock")
                : (lang === "gr" ? "Υποβολή αποθέματος" : "Submit closing stock")}
          </button>
        </>
      )}
    </div>
  );
}

